import type { Plan } from './plans';

/** The networks a plan can be sold on. Empty on a record means DGtek. */
export type PlanNetwork = 'dgtek' | 'communityfibre';

export const PLAN_NETWORKS: { value: PlanNetwork; label: string }[] = [
  { value: 'dgtek', label: 'DGtek' },
  { value: 'communityfibre', label: 'Community Fibre' },
];

export const networkLabel = (network: PlanNetwork): string => (
  PLAN_NETWORKS.find((n) => n.value === network)?.label ?? network
);

export const networkOf = (plan: Plan): PlanNetwork => (
  plan.network === 'communityfibre' ? 'communityfibre' : 'dgtek'
);

/** One access technology a wholesale product id is needed for. */
export interface TechnologyOption {
  key: string;
  /** Short label for a summary, e.g. the plans list. */
  short: string;
  /** Field label in the editor. */
  label: string;
  placeholder: string;
}

const COMMUNITY_FIBRE_TECHNOLOGIES: TechnologyOption[] = [
  {
    key: 'fttp', short: 'fibre', label: 'Fibre (XGS-PON)', placeholder: 'SKU1001434',
  },
  {
    key: 'gfast', short: 'G.fast', label: 'G.fast', placeholder: 'SKU1001368',
  },
];

/**
 * The access technologies the network sells on, and so the wholesale ids a
 * plan on it can carry. DGtek is Pineapple's own network and has none.
 */
export const technologiesFor = (network: PlanNetwork): TechnologyOption[] => (
  network === 'communityfibre' ? COMMUNITY_FIBRE_TECHNOLOGIES : []
);

const shortOf = (key: string): string => (
  COMMUNITY_FIBRE_TECHNOLOGIES.find((t) => t.key === key)?.short ?? key
);

export const wholesaleErrors = (plan: Plan): string[] => {
  const network = networkOf(plan);
  const technologies = technologiesFor(network);
  const ids = plan.external_plan_ids ?? {};
  const errors: string[] = [];
  const unknown = new Set<string>();
  [ids, plan.layer2_plan_ids ?? {}].forEach((set) => {
    Object.keys(set).forEach((key) => {
      if (!technologies.some((t) => t.key === key)) unknown.add(key);
    });
  });
  unknown.forEach((key) => {
    errors.push(`${networkLabel(network)} has no access technology "${key}"`);
  });
  const set = technologies.filter((t) => (ids[t.key] ?? '').trim() !== '');
  if (technologies.length > 0 && plan.published && set.length === 0) {
    errors.push(`A published ${networkLabel(network)} plan needs at least one wholesale product id`);
  }
  return errors;
};

/**
 * The plan as it should be stored: wholesale ids trimmed, empty ones and
 * ids for technologies the network does not have dropped, DGtek left with
 * an empty network as its records always were, and download carried
 * alongside upload so an asymmetric plan describes itself.
 */
export const normalisePlan = (plan: Plan): Plan => {
  const network = networkOf(plan);
  const trimmed = (given?: Record<string, string>): Record<string, string> => {
    const ids: Record<string, string> = {};
    technologiesFor(network).forEach((t) => {
      const value = (given?.[t.key] ?? '').trim();
      if (value !== '') ids[t.key] = value;
    });
    return ids;
  };
  const ids = trimmed(plan.external_plan_ids);
  const upload = plan.upload > 0 ? plan.upload : 0;
  return {
    ...plan,
    rrp: (plan.rrp ?? '').trim() || '0',
    promo_cost: (plan.promo_cost ?? '').trim() || '0',
    card_points: (plan.card_points ?? []).map((point) => point.trim()),
    // The Chargebee plan is the one with the plan id; a plan stored while it
    // could be pointed at another is brought back to its own.
    ...({ chargebee_plan_id: '' } as object),
    network: network === 'dgtek' ? '' : network,
    download: upload > 0 ? plan.speed : 0,
    upload,
    external_plan_ids: ids,
    layer2_plan_ids: trimmed(plan.layer2_plan_ids),
  };
};

export const externalPlanIdsSummary = (ids?: Record<string, string>): string => {
  const entries = Object.entries(ids ?? {}).filter(([, v]) => v.trim() !== '');
  if (entries.length === 0) return 'none';
  return entries.map(([key, value]) => `${shortOf(key)} ${value}`).join(', ');
};

export const sameExternalPlanIds = (a?: Record<string, string>, b?: Record<string, string>): boolean => {
  const left = Object.entries(a ?? {}).sort(([x], [y]) => x.localeCompare(y));
  const right = Object.entries(b ?? {}).sort(([x], [y]) => x.localeCompare(y));
  return JSON.stringify(left) === JSON.stringify(right);
};
